import { createSelector } from '@reduxjs/toolkit';
import { t } from '../../utilities/translator';
import format from '../../utilities/format';
import { parseDate } from '../../utilities/helpers';
import { selectSearch } from './searchSlice';

export const selectFilterData = (state) => state.mainFilter;
export const selectDateFilter = (state) => state.mainFilter.searchEngine?.when;
export const selectMonthAhead = (state) => state.mainFilter.searchEngine?.monthAhead;
export const specificDateSelected = (state) => !!(state.search.when?.type === 'specific' && state.search.when?.startDate && state.search.when?.endDate);

export const selectActiveDestinations = createSelector(
  [(state) => state.mainFilter.searchEngine],
  (searchEngine) => {
    if (!searchEngine.where.destinations) return [];

    return searchEngine.where.destinations.filter(({ active }) => active);
  }
);

export const selectActiveDepartures = createSelector(
  [(state) => state.mainFilter.searchEngine],
  (searchEngine) => {
    if (!searchEngine.fromwhere) return [];

    return searchEngine.fromwhere.filter(({ active }) => active);
  }
);

export const selectActiveDestinationGroups = createSelector(
  [(state) => state.mainFilter.searchEngine],
  (searchEngine) => {
    if (!searchEngine.where.destinationsGroups) return [];

    return searchEngine.where.destinationsGroups.filter(({ active }) => active);
  }
)

export const selectActiveDestination = (state) =>
  state.mainFilter.searchEngine.where?.destinations?.find(
    (item) => item.active
  );

export const specificCitySelectedSelector = createSelector(
  selectActiveDestinations,
  (destinations) => destinations.length === 1
);

export const selectFiltersLoaded = (state) =>
  !state.mainFilter.pending &&
  !state.mainFilter.error &&
  !state.secondaryFilter.pending &&
  !state.secondaryFilter.error;

export const selectDatesLabel = createSelector(
  [(state) => state.mainFilter.searchEngine],
  ({ when }) => {
    if (Object.keys(when).length === 0) {
      return t('mainFilter.any_dates');
    }

    if (when.type === 'month' && when.months?.length === 0 && when.nights?.length === 0) {
      return t('mainFilter.any_dates');
    }

    const { type, startDate, endDate, months, nights } = when;
    const getStartDate = startDate
      ? format(startDate, 'dd MMM')
      : t('mainFilter.any');
    const getEndDate = endDate
      ? format(endDate, 'dd MMM')
      : t('mainFilter.any');

    let nightsLabel = '';
    if (nights?.length > 0) {
      nightsLabel = ` - ${nights.join(', ')} ${t(
        'dealItem.nights_plural',
        { count: 2 }
      )}`;
    }

    let label = '';

    if (type === 'specific') label += `${getStartDate} - ${getEndDate}`;
    if (type === 'range') label += `${getStartDate} - ${getEndDate}`;
    if (type === 'month') {
      let monthLabel = '';
      if (months.length > 0) {
        monthLabel = `${[...months]
          .sort((a, b) => a - b)
          .map((m) => format(parseDate(m, 'M'), 'MMM'))
          .join(', ')}`;
      } else {
        monthLabel = t('mainFilter.monthsPlaceholder');
      }
      label += monthLabel;
    }
    if (type === 'month' || type === 'range') {
      label += nightsLabel;
    }

    return label;
  }
)

export const selectActiveThemes = createSelector(
  [(state) => state.mainFilter.searchEngine],
  ({ what }) => {
    if (!what) return [];

    return what.filter(({ active }) => active);
  }
)

export const selectDestinations = createSelector(
  [selectFilterData],
  (filter) => filter.searchEngine.where.destinations
)

export const selectDepartures = createSelector(
  [selectFilterData],
  (filter) => filter.searchEngine.fromwhere
)
